import React, { useState, useEffect } from 'react';
import { CalendarDays, X } from 'lucide-react';
import { useAuth } from '../store/auth';
import { useInventory } from '../store/inventory';
import { supabase } from '../lib/supabase';
import { GroupMember } from '../types';
import { Avatar, BottomSheet, Field, Input, SecondaryBtn, Spinner } from './UI';
import { CategoryIcon } from '../lib/categoryIcons';

export const LoanOutSheet: React.FC<{ presetItemId?: string; onClose: () => void }> = ({ presetItemId, onClose }) => {
  const { user } = useAuth();
  const { items, fetchItems, loanOutItem } = useInventory();
  const [selectedItemId, setSelectedItemId] = useState<string | null>(presetItemId || null);
  const defaultDueDate = () => { const d = new Date(); d.setDate(d.getDate() + 14); return d.toISOString().split('T')[0]; };
  const [dueDate, setDueDate] = useState(defaultDueDate);
  const [loanCandidates, setLoanCandidates] = useState<GroupMember[]>([]);
  const [loanCandidatesLoading, setLoanCandidatesLoading] = useState(false);

  useEffect(() => {
    if (user) fetchItems(user.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const loanableItems = items.filter(i => i.user_id === user?.id && i.loanable && i.status === 'available');
  const selectedItem = selectedItemId ? items.find(i => i.id === selectedItemId) : null;

  useEffect(() => {
    if (!selectedItemId || !user) { setLoanCandidates([]); return; }
    let cancelled = false;
    setLoanCandidatesLoading(true);
    (async () => {
      const { data: links } = await supabase.from('inventory_groups').select('group_id').eq('inventory_id', selectedItemId);
      const groupIds = [...new Set((links || []).map((l: any) => l.group_id))];
      if (groupIds.length === 0) {
        if (!cancelled) { setLoanCandidates([]); setLoanCandidatesLoading(false); }
        return;
      }
      const { data: mems } = await supabase.from('group_members').select('*, profile:profiles(*)').in('group_id', groupIds);
      const seen = new Set<string>();
      const unique = (mems || []).filter((m: any) => {
        if (m.user_id === user.id || !m.profile || seen.has(m.user_id)) return false;
        seen.add(m.user_id);
        return true;
      });
      if (!cancelled) { setLoanCandidates(unique); setLoanCandidatesLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [selectedItemId, user]);

  if (!selectedItem) {
    return (
      <BottomSheet title="Lån ut et verktøy" onClose={onClose}>
        {loanableItems.length === 0 ? (
          <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 14 }}>Du har ingen ledige, lånbare ting akkurat nå.</p>
        ) : (
          <>
            <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 14 }}>Hvilken ting vil du låne ut?</p>
            {loanableItems.map(item => (
              <div key={item.id} onClick={() => setSelectedItemId(item.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 13, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', marginBottom: 8, cursor: 'pointer' }}>
                {item.bilde_url
                  ? <img src={item.bilde_url} alt={item.navn} style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                  : <div style={{ width: 44, height: 44, borderRadius: 8, background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <CategoryIcon emoji={item.emoji} size={20} color="var(--green)" />
                    </div>
                }
                <div style={{ flex: 1, fontWeight: 500 }}>{item.navn}</div>
                <span style={{ color: 'var(--text3)' }}>›</span>
              </div>
            ))}
          </>
        )}
        <SecondaryBtn onClick={onClose}>Avbryt</SecondaryBtn>
      </BottomSheet>
    );
  }

  return (
    <BottomSheet title={`Lån ut ${selectedItem.navn}`} onClose={onClose}>
      <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 14 }}>Hvem låner dette?</p>
      <Field label="Frist for tilbakelevering">
        <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
          {[1, 2, 3, 4].map(w => {
            const d = new Date(); d.setDate(d.getDate() + w * 7);
            const v = d.toISOString().split('T')[0];
            const active = dueDate === v;
            return (
              <button key={w} type="button" onClick={() => setDueDate(v)}
                style={{ padding: '5px 13px', borderRadius: 30, fontSize: 13, fontWeight: 600, border: `1px solid ${active ? 'var(--green)' : 'var(--border-strong)'}`, background: active ? 'var(--green-light)' : 'var(--bg)', color: active ? 'var(--green-dark)' : 'var(--text2)', cursor: 'pointer' }}>
                {w}u
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ position: 'relative', flex: 'none' }}>
            <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} min={new Date().toISOString().split('T')[0]} style={{ width: 'auto', minWidth: 160, paddingLeft: 38 }} />
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', display: 'flex', pointerEvents: 'none', color: 'var(--text3)', zIndex: 1 }}><CalendarDays size={16} strokeWidth={1.8} /></span>
          </div>
          {dueDate && <button type="button" onClick={() => setDueDate('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', display: 'flex', padding: 4 }}><X size={16} strokeWidth={2} /></button>}
        </div>
      </Field>
      {loanCandidatesLoading ? (
        <Spinner />
      ) : loanCandidates.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 14 }}>Ingen andre medlemmer har tilgang til denne tingen ennå — del den med en gruppe først.</p>
      ) : loanCandidates.map(m => {
        const p = m.profile!;
        return (
          <div key={m.id} onClick={() => { loanOutItem(selectedItem.id, m.user_id, dueDate || undefined); onClose(); }}
            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 13, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', marginBottom: 8, cursor: 'pointer' }}>
            <Avatar name={p.full_name} color={p.avatar_color} size="md" />
            <div style={{ flex: 1, fontWeight: 500 }}>{p.full_name}</div>
            <span style={{ color: 'var(--text3)' }}>›</span>
          </div>
        );
      })}
      <SecondaryBtn onClick={onClose}>Avbryt</SecondaryBtn>
    </BottomSheet>
  );
};
